// =========================================================
// Vojjin AJAX Utilities
// =========================================================
import {registerGlobal} from "./core.js";

/* global Logger */

class Net {

    /**
     * Fetch utility for rock framework.
     * Supports method, query params, body, headers, and error handling.
     *
     * @param {string} url - The endpoint URL
     * @param {Object} [options] - Optional configuration
     * @param {string} [options.method='GET'] - HTTP method
     * @param {Object} [options.query] - Query parameters (for GET-like requests)
     * @param {Object|string|FormData|null} [options.body] - Request payload
     * @param {Object} [options.headers] - Custom headers
     * @returns {Promise<Object>} - Resolves with parsed JSON or response text
     *
     * sample:
     * rock.net.fetch("https://api.example.com/login", {
     *   method: "POST",
     *   body: { username: "traveler", password: "magic" },
     * }).then(data => {
     *   console.log("Success:", data);
     * }).catch(err => {
     *   console.error("Failed:", err);
     * });
     */
    static fetch = async (url, options = {}) => {
        const {
            method = "GET",
            query = null,
            body = null,
            headers = {},
        } = options;

        // Build URL with query params
        let finalUrl = url;
        if (query && typeof query === "object") {
            const qs = new URLSearchParams(query).toString();
            finalUrl += (url.includes("?") ? "&" : "?") + qs;
        }

        const fetchOptions = {
            method: method.toUpperCase(),
            headers: {
                Accept: "application/json",
                ...headers,
            },
        };

        // Handle body
        if (body) {
            if (body instanceof FormData) {
                // ✅ DO NOT set Content-Type for FormData
                fetchOptions.body = body;
            } else {
                fetchOptions.headers["Content-Type"] = "application/json";
                fetchOptions.body = JSON.stringify(body);
            }
        }

        try {
            const response = await fetch(finalUrl, fetchOptions);
            const text = await response.text();


            // Try JSON, fallback to text
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                data = text;
            }

            if (!response.ok) {
                throw text;
            }
            return data;
        } catch (err) {
            Logger.log("Fetch failed:", err);
            throw err; // ✅ CRITICAL
        }
    };


    static fetch_old = async (url, options = {}) => {
        const {
            method = "GET",
            query = null,
            body = null,
            headers = {},
        } = options;

        try {
            // Construct query params if present
            let finalUrl = url;
            if (query && typeof query === "object") {
                const qs = new URLSearchParams(query).toString();
                finalUrl += (url.includes("?") ? "&" : "?") + qs;
            }

            // Default headers
            const finalHeaders = {
                "Content-Type": "application/json",
                "Accept": "application/json",
                ...headers,
            };

            // Prepare fetch options
            const fetchOptions = {
                method: method.toUpperCase(),
                headers: finalHeaders,
            };

            // Handle request body (JSON auto-stringify)
            if (body) {
                if (
                    finalHeaders["Content-Type"] &&
                    finalHeaders["Content-Type"].includes("application/json") &&
                    typeof body === "object" &&
                    !(body instanceof FormData)
                ) {
                    fetchOptions.body = JSON.stringify(body);
                } else {
                    fetchOptions.body = body;
                }
            }

            try {
                const response = await fetch(finalUrl, fetchOptions);
                // Check for network or HTTP errors
                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(
                        `HTTP ${response.status} ${response.statusText}: ${text}`
                    );
                } else {
                    const text = await response.text();
                    let json;
                    try {
                        json = JSON.parse(text);
                        console.log("Response parsed as JSON");
                        return json;
                    } catch (err) {
                        console.log("Response is NOT JSON");
                        return text;
                    }
                }

            } catch (e) {
                Logger.log("Fetch failed at fetch() call:", e);
            }
        } catch (err) {
            Logger.log("Fetch failed at fetch() call:", err);
            throw err;
        }
    };

    /**
     * Sends a GET request to the specified URL with the provided query parameters and headers.
     *
     * @param {string} url - The URL to send the GET request to.
     * @param {Object} [query] - An optional object containing query parameters to be appended to the URL.
     * @param {Object} [headers] - An optional object containing headers to be included in the request.
     * @returns {Promise<any>} A promise that resolves with the response of the GET request.
     */
    static get = (url, query, headers) =>
        this.fetch(url, {method: "GET", query: query, headers: headers});

    /**
     * Sends an HTTP POST request to the specified URL with the provided body and headers.
     *
     * @param {string} url - The URL to which the POST request is sent.
     * @param {string|Object} body - The body of the POST request. Can be a string or an object, depending on the content type.
     * @param {Object} headers - An object representing the headers to include in the request.
     * @returns {Promise<Response>} A promise that resolves to the response of the fetch request.
     */
    static post = (url, body, headers) =>
        this.fetch(url, {method: "POST", body: body, headers: headers});


    static postError = () => {
        removeBlack();
        toster.show({message: 'Error connecting to server!', type: 'danger'});
    }

}

registerGlobal("Net", Net);
